import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Brain, Wand2, Save, Plus, Search, Filter, BookOpen, Clock, Target, Settings, FileText, Link, ImageIcon, Sparkles, Eye, Edit, Trash2, Download, FileDown, Users, Calendar, Star } from 'lucide-react';
import { geminiService } from '@/services/geminiService';
import { useToast } from '@/hooks/use-toast';
import QuestionSelector from './QuestionSelector';
import ResourceBasedQuizGenerator from './ResourceBasedQuizGenerator';

interface Question {
  id: number;
  teacher_id: number;
  question_text: string;
  answer_text: string;
  question_type: 'multiple_choice' | 'true_false' | 'short_answer' | 'essay';
  subject: string;
  difficulty: 'easy' | 'medium' | 'hard';
  tags: string[];
  source_type: string;
  source_data: any;
  created_at: string;
  updated_at: string;
}

interface GeneratedQuestion {
  question: string;
  options?: string[];
  correctAnswer: string;
  type: 'multiple_choice' | 'true_false' | 'short_answer' | 'essay';
  explanation?: string;
}

interface Quiz {
  title: string;
  topic: string;
  difficulty: 'easy' | 'medium' | 'hard';
  questions: GeneratedQuestion[];
  timeLimit: number;
  classId?: number;
}

interface SavedQuiz {
  id: number;
  teacher_id: number;
  class_id: number;
  title: string;
  topic: string;
  difficulty: 'easy' | 'medium' | 'hard';
  total_questions: number;
  time_limit: number;
  created_at?: string;
  updated_at?: string;
  class_name?: string;
  questions?: QuizQuestion[];
}

interface QuizQuestion {
  id: number;
  quiz_id: number;
  question_text: string;
  question_type: 'multiple_choice' | 'true_false' | 'short_answer' | 'essay';
  options: string;
  correct_answer: string;
  points: number;
  question_order: number;
}

const QuizGenerator: React.FC = () => {
  // Quiz generation state
  const [quizData, setQuizData] = useState({
    title: '',
    topic: '',
    gradeLevel: '',
    numQuestions: 10,
    difficulty: 'medium' as 'easy' | 'medium' | 'hard',
    timeLimit: 30,
    classId: 'none'
  });

  // Pool selection state
  const [selectedPoolQuestions, setSelectedPoolQuestions] = useState<Question[]>([]);
  const [poolFilters, setPoolFilters] = useState({
    category: 'all',
    difficulty: 'all',
    tags: ''
  });

  // Generated quiz state
  const [generatedQuestions, setGeneratedQuestions] = useState<GeneratedQuestion[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [classes, setClasses] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState('ai-generate');
  const [isQuestionSelectorOpen, setIsQuestionSelectorOpen] = useState(false);

  // Saved quizzes state
  const [savedQuizzes, setSavedQuizzes] = useState<SavedQuiz[]>([]);
  const [isLoadingSavedQuizzes, setIsLoadingSavedQuizzes] = useState(false);
  const [quizDetailView, setQuizDetailView] = useState<SavedQuiz | null>(null);
  const [isQuizDetailOpen, setIsQuizDetailOpen] = useState(false);
  const [savedQuizFilters, setSavedQuizFilters] = useState({
    search: '',
    difficulty: 'all',
    class: 'all'
  });

  const { toast } = useToast();

  // Table IDs
  const QUESTIONS_POOL_TABLE_ID = '16057';
  const QUIZZES_TABLE_ID = '15204';
  const QUIZ_QUESTIONS_TABLE_ID = '15207';
  const CLASSES_TABLE_ID = '15198';

  useEffect(() => {
    fetchClasses();
    if (activeTab === 'saved-quizzes') {
      fetchSavedQuizzes();
    }
  }, [activeTab]);

  const fetchClasses = async () => {
    try {
      const { data: userInfo, error: userError } = await window.ezsite.apis.getUserInfo();
      if (userError) throw userError;

      const { data, error } = await window.ezsite.apis.tablePage(CLASSES_TABLE_ID, {
        PageNo: 1,
        PageSize: 100,
        OrderByField: 'class_name',
        IsAsc: true,
        Filters: [
        { name: 'teacher_id', op: 'Equal', value: userInfo.ID }]

      });

      if (error) throw error;
      setClasses(data.List || []);
    } catch (error) {
      console.error('Error fetching classes:', error);
    }
  };

  const fetchSavedQuizzes = async () => {
    setIsLoadingSavedQuizzes(true);
    try {
      const { data: userInfo, error: userError } = await window.ezsite.apis.getUserInfo();
      if (userError) throw userError;

      const filters = [
      { name: 'teacher_id', op: 'Equal', value: userInfo.ID }];


      // Add search filter
      if (savedQuizFilters.search) {
        filters.push({ name: 'title', op: 'StringContains', value: savedQuizFilters.search });
      }

      // Add difficulty filter
      if (savedQuizFilters.difficulty !== 'all') {
        filters.push({ name: 'difficulty', op: 'Equal', value: savedQuizFilters.difficulty });
      }

      // Add class filter
      if (savedQuizFilters.class !== 'all') {
        filters.push({ name: 'class_id', op: 'Equal', value: parseInt(savedQuizFilters.class) });
      }

      const { data, error } = await window.ezsite.apis.tablePage(QUIZZES_TABLE_ID, {
        PageNo: 1,
        PageSize: 100,
        OrderByField: 'updated_at',
        IsAsc: false,
        Filters: filters
      });

      if (error) throw error;

      // Enhance quizzes with class names
      const quizzesWithClassNames = (data.List || []).map((quiz: SavedQuiz) => ({
        ...quiz,
        class_name: classes.find((cls) => cls.id === quiz.class_id)?.class_name || 'No Class'
      }));

      setSavedQuizzes(quizzesWithClassNames);
    } catch (error) {
      console.error('Error fetching saved quizzes:', error);
      toast({
        title: "Error",
        description: "Failed to load saved quizzes. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoadingSavedQuizzes(false);
    }
  };

  const fetchQuizDetails = async (quizId: number) => {
    try {
      // Fetch quiz questions
      const { data, error } = await window.ezsite.apis.tablePage(QUIZ_QUESTIONS_TABLE_ID, {
        PageNo: 1,
        PageSize: 100,
        OrderByField: 'question_order',
        IsAsc: true,
        Filters: [{ name: 'quiz_id', op: 'Equal', value: quizId }]
      });

      if (error) throw error;

      const quiz = savedQuizzes.find((q) => q.id === quizId);
      if (quiz) {
        setQuizDetailView({
          ...quiz,
          questions: data.List || []
        });
        setIsQuizDetailOpen(true);
      }
    } catch (error) {
      console.error('Error fetching quiz details:', error);
      toast({
        title: "Error",
        description: "Failed to load quiz details. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleDeleteQuiz = async (quizId: number) => {
    try {
      // Delete quiz questions first
      const { data: questions, error: questionsError } = await window.ezsite.apis.tablePage(QUIZ_QUESTIONS_TABLE_ID, {
        PageNo: 1,
        PageSize: 100,
        OrderByField: 'id',
        IsAsc: true,
        Filters: [{ name: 'quiz_id', op: 'Equal', value: quizId }]
      });

      if (questionsError) throw questionsError;

      // Delete each question
      if (questions.List) {
        for (const question of questions.List) {
          await window.ezsite.apis.tableDelete(QUIZ_QUESTIONS_TABLE_ID, { ID: question.id });
        }
      }

      // Delete the quiz
      const { error: quizError } = await window.ezsite.apis.tableDelete(QUIZZES_TABLE_ID, { ID: quizId });
      if (quizError) throw quizError;

      toast({
        title: "Quiz Deleted",
        description: "Quiz has been deleted successfully."
      });

      // Refresh the list
      fetchSavedQuizzes();
    } catch (error) {
      console.error('Error deleting quiz:', error);
      toast({
        title: "Delete Failed",
        description: "Failed to delete quiz. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleEditQuiz = (quiz: SavedQuiz) => {
    // Pre-fill the form with quiz data for editing
    setQuizData({
      title: quiz.title,
      topic: quiz.topic,
      gradeLevel: '', // This might not be stored
      numQuestions: quiz.total_questions,
      difficulty: quiz.difficulty,
      timeLimit: quiz.time_limit,
      classId: quiz.class_id ? quiz.class_id.toString() : 'none'
    });

    // Switch to AI generate tab for editing
    setActiveTab('ai-generate');

    toast({
      title: "Quiz Loaded for Editing",
      description: "Quiz data has been loaded. Make your changes and regenerate if needed."
    });
  };

  const generateQuestionsFile = (questions: GeneratedQuestion[], title: string) => {
    let content = `${title}\nQuestions Only\n\n`;
    content += `Instructions: Answer all questions to the best of your ability.\n`;
    content += `Time Limit: ${quizData.timeLimit} minutes\n`;
    content += `Total Questions: ${questions.length}\n\n`;
    content += '=' + '='.repeat(50) + '\n\n';

    questions.forEach((question, index) => {
      content += `${index + 1}. ${question.question}\n\n`;

      if (question.type === 'multiple_choice' && question.options) {
        question.options.forEach((option, optIndex) => {
          content += `   ${String.fromCharCode(65 + optIndex)}. ${option}\n`;
        });
        content += '\n';
      } else if (question.type === 'true_false') {
        content += '   A. True\n   B. False\n\n';
      } else if (question.type === 'short_answer') {
        content += '   Answer: ________________________\n\n';
      } else if (question.type === 'essay') {
        content += '   Use the space below to write your essay:\n\n';
        content += '   ' + '_'.repeat(50) + '\n\n';
        content += '   ' + '_'.repeat(50) + '\n\n';
        content += '   ' + '_'.repeat(50) + '\n\n';
      }

      content += '\n';
    });

    return content;
  };

  const generateAnswerKeyFile = (questions: GeneratedQuestion[], title: string) => {
    let content = `${title}\nAnswer Key\n\n`;
    content += `Difficulty: ${quizData.difficulty.charAt(0).toUpperCase() + quizData.difficulty.slice(1)}\n`;
    content += `Topic: ${quizData.topic}\n`;
    content += `Total Questions: ${questions.length}\n\n`;
    content += '=' + '='.repeat(50) + '\n\n';

    questions.forEach((question, index) => {
      content += `${index + 1}. ${question.question}\n\n`;

      content += `   Type: ${question.type.replace('_', ' ').toUpperCase()}\n`;
      content += `   Correct Answer: ${question.correctAnswer}\n`;

      if (question.explanation && question.explanation !== question.correctAnswer) {
        content += `   Explanation: ${question.explanation}\n`;
      }

      if (question.type === 'multiple_choice' && question.options) {
        content += `   Options:\n`;
        question.options.forEach((option, optIndex) => {
          const letter = String.fromCharCode(65 + optIndex);
          const isCorrect = option === question.correctAnswer || letter === question.correctAnswer;
          content += `      ${letter}. ${option}${isCorrect ? ' ✓' : ''}\n`;
        });
      }

      content += '\n' + '-'.repeat(60) + '\n\n';
    });

    return content;
  };

  const downloadFile = (content: string, filename: string) => {
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleDownloadQuestions = () => {
    if (generatedQuestions.length === 0) {
      toast({
        title: "No Questions",
        description: "Please generate questions first.",
        variant: "destructive"
      });
      return;
    }

    const content = generateQuestionsFile(generatedQuestions, quizData.title);
    const filename = `${quizData.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_questions.txt`;
    downloadFile(content, filename);

    toast({
      title: "Questions Downloaded",
      description: "Questions file has been downloaded successfully."
    });
  };

  const handleDownloadAnswerKey = () => {
    if (generatedQuestions.length === 0) {
      toast({
        title: "No Questions",
        description: "Please generate questions first.",
        variant: "destructive"
      });
      return;
    }

    const content = generateAnswerKeyFile(generatedQuestions, quizData.title);
    const filename = `${quizData.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_answer_key.txt`;
    downloadFile(content, filename);

    toast({
      title: "Answer Key Downloaded",
      description: "Answer key file has been downloaded successfully."
    });
  };

  const handleDownloadSavedQuizQuestions = async (quiz: SavedQuiz) => {
    try {
      // Fetch quiz questions
      const { data, error } = await window.ezsite.apis.tablePage(QUIZ_QUESTIONS_TABLE_ID, {
        PageNo: 1,
        PageSize: 100,
        OrderByField: 'question_order',
        IsAsc: true,
        Filters: [{ name: 'quiz_id', op: 'Equal', value: quiz.id }]
      });

      if (error) throw error;

      // Convert quiz questions to GeneratedQuestion format
      const questions: GeneratedQuestion[] = (data.List || []).map((q: QuizQuestion) => ({
        question: q.question_text,
        options: q.options ? JSON.parse(q.options) : undefined,
        correctAnswer: q.correct_answer,
        type: q.question_type,
        explanation: q.correct_answer
      }));

      const content = generateQuestionsFile(questions, quiz.title);
      const filename = `${quiz.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_questions.txt`;
      downloadFile(content, filename);

      toast({
        title: "Questions Downloaded",
        description: "Questions file has been downloaded successfully."
      });
    } catch (error) {
      console.error('Error downloading questions:', error);
      toast({
        title: "Download Failed",
        description: "Failed to download questions. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleDownloadSavedQuizAnswerKey = async (quiz: SavedQuiz) => {
    try {
      // Fetch quiz questions
      const { data, error } = await window.ezsite.apis.tablePage(QUIZ_QUESTIONS_TABLE_ID, {
        PageNo: 1,
        PageSize: 100,
        OrderByField: 'question_order',
        IsAsc: true,
        Filters: [{ name: 'quiz_id', op: 'Equal', value: quiz.id }]
      });

      if (error) throw error;

      // Convert quiz questions to GeneratedQuestion format
      const questions: GeneratedQuestion[] = (data.List || []).map((q: QuizQuestion) => ({
        question: q.question_text,
        options: q.options ? JSON.parse(q.options) : undefined,
        correctAnswer: q.correct_answer,
        type: q.question_type,
        explanation: q.correct_answer
      }));

      // Use quiz data for answer key generation
      const tempQuizData = {
        title: quiz.title,
        topic: quiz.topic,
        difficulty: quiz.difficulty,
        timeLimit: quiz.time_limit
      };

      const content = generateAnswerKeyFile(questions, quiz.title);
      const filename = `${quiz.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_answer_key.txt`;
      downloadFile(content, filename);

      toast({
        title: "Answer Key Downloaded",
        description: "Answer key file has been downloaded successfully."
      });
    } catch (error) {
      console.error('Error downloading answer key:', error);
      toast({
        title: "Download Failed",
        description: "Failed to download answer key. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleGenerateQuiz = async () => {
    if (!quizData.title || !quizData.topic) {
      toast({
        title: "Missing Information",
        description: "Please fill in the quiz title and topic.",
        variant: "destructive"
      });
      return;
    }

    setIsGenerating(true);
    setGenerationProgress(10);

    try {
      // Simulate progress updates
      const progressInterval = setInterval(() => {
        setGenerationProgress((prev) => Math.min(prev + 20, 90));
      }, 500);

      const questionTypes = ['multiple_choice', 'true_false', 'short_answer'];
      const additionalInstructions = `Generate ${quizData.numQuestions} quiz questions about ${quizData.topic} for ${quizData.gradeLevel} grade level with ${quizData.difficulty} difficulty.`;

      const aiResponse = await geminiService.generateQuiz(
        quizData.topic,
        quizData.numQuestions,
        quizData.difficulty,
        questionTypes,
        additionalInstructions
      );

      clearInterval(progressInterval);
      setGenerationProgress(100);

      if (aiResponse.error) {
        throw new Error(aiResponse.error);
      }

      console.log('🎯 Raw AI Response:', aiResponse.text);

      // Use the improved JSON parsing method
      const parseResult = geminiService.parseJsonResponse(aiResponse.text);
      if (parseResult.error) {
        console.log('❌ JSON parsing failed, trying text format...');
        // Fall back to text parsing if JSON parsing fails
        const questions = parseAIResponseAsText(aiResponse.text);
        setGeneratedQuestions(questions);
      } else {
        // Extract questions from parsed JSON
        const questionsArray = parseResult.data.questions || parseResult.data;
        const questions = questionsArray.map((q: any) => ({
          question: q.question || q.question_text || '',
          options: q.options || undefined,
          correctAnswer: q.correctAnswer || q.correct_answer || q.answer || '',
          type: q.type || q.question_type || 'short_answer',
          explanation: q.explanation || q.answer || ''
        })).filter((q: GeneratedQuestion) => q.question && q.correctAnswer);

        setGeneratedQuestions(questions);
      }

      console.log('✅ Final questions count:', generatedQuestions.length);

      toast({
        title: "Quiz Generated",
        description: `Successfully generated ${generatedQuestions.length} questions!`
      });

    } catch (error) {
      console.error('Error generating quiz:', error);
      toast({
        title: "Generation Failed",
        description: `Failed to generate quiz: ${error instanceof Error ? error.message : 'Please try again.'}`,
        variant: "destructive"
      });
    } finally {
      setIsGenerating(false);
      setGenerationProgress(0);
    }
  };

  const handleCreateFromPool = () => {
    if (selectedPoolQuestions.length === 0) {
      toast({
        title: "No Questions Selected",
        description: "Please select questions from the pool.",
        variant: "destructive"
      });
      return;
    }

    if (!quizData.title || !quizData.topic) {
      toast({
        title: "Missing Information",
        description: "Please fill in the quiz title and topic.",
        variant: "destructive"
      });
      return;
    }

    const questions: GeneratedQuestion[] = selectedPoolQuestions.map((q) => ({
      question: q.question_text,
      options: q.source_data?.options || undefined,
      correctAnswer: q.answer_text,
      type: q.question_type,
      explanation: q.answer_text
    }));

    setGeneratedQuestions(questions);
    toast({
      title: "Quiz Created",
      description: `Created quiz with ${questions.length} questions from pool!`
    });
  };

  const handleQuestionsSelected = (questions: Question[]) => {
    setSelectedPoolQuestions(questions);
    setIsQuestionSelectorOpen(false);

    // Auto-create quiz if we have basic info
    if (quizData.title && quizData.topic) {
      const generatedQuestions: GeneratedQuestion[] = questions.map((q) => ({
        question: q.question_text,
        options: q.source_data?.options || undefined,
        correctAnswer: q.answer_text,
        type: q.question_type,
        explanation: q.answer_text
      }));

      setGeneratedQuestions(generatedQuestions);
      toast({
        title: "Quiz Created",
        description: `Created quiz with ${questions.length} questions from pool!`
      });
    }
  };

  const handleResourceQuestionsGenerated = (questions: GeneratedQuestion[]) => {
    setGeneratedQuestions(questions);
    toast({
      title: "Quiz Generated from Resource",
      description: `Successfully generated ${questions.length} questions from your resource!`
    });
  };

  const parseAIResponseAsText = (response: string): GeneratedQuestion[] => {
    console.log('📄 Starting to parse AI response as text, length:', response.length);

    // Clean up the response
    const cleanedResponse = response.trim().replace(/\r/g, '');
    console.log('🧹 Cleaned response preview:', cleanedResponse.substring(0, 200));

    // Enhanced text parsing with multiple strategies
    const questions: GeneratedQuestion[] = [];
    const lines = cleanedResponse.split('\n').map((line) => line.trim()).filter((line) => line);

    console.log('📝 Processing text lines:', lines.length);

    let currentQuestion: Partial<GeneratedQuestion> = {};
    let isInQuestion = false;
    let isInOptions = false;
    let optionsList: string[] = [];

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      console.log(`🔍 Line ${i + 1}:`, line);

      // Skip markdown headers and decorative lines
      if (line.startsWith('#') || line.match(/^[=\-_\*]{3,}$/) || line.toLowerCase() === 'quiz questions') {
        console.log('⏩ Skipping decorative line');
        continue;
      }

      // Match question patterns - more flexible regex
      const questionPatterns = [
      /^(?:Q(?:uestion)?[\s\.]*)?(\\d+)[\\.\\:\\)\\s]+(.*)/i,
      /^(\\d+)[\\.\\:\\)\\s]+(.*)/,
      /^Question\\s+(\\d+):?\\s*(.*)/i,
      /^(\\d+)\\s*[\\)\\.\\-]\\s*(.*)/];


      let questionMatch = null;
      for (const pattern of questionPatterns) {
        questionMatch = line.match(pattern);
        if (questionMatch) break;
      }

      if (questionMatch) {
        // Save previous question if it exists
        if (currentQuestion.question) {
          console.log('✅ Saving previous question:', currentQuestion.question.substring(0, 50));
          questions.push({
            question: currentQuestion.question,
            correctAnswer: currentQuestion.correctAnswer || 'Answer not provided',
            type: currentQuestion.type || 'short_answer',
            explanation: currentQuestion.explanation,
            options: currentQuestion.options
          });
        }

        // Start new question
        const questionText = questionMatch[2].trim();
        currentQuestion = {
          question: questionText,
          type: 'short_answer'
        };
        isInQuestion = true;
        isInOptions = false;
        optionsList = [];

        console.log(`📝 Found new question ${questionMatch[1]}: ${questionText.substring(0, 50)}`);
        continue;
      }

      // Look for multiple choice options - Enhanced patterns
      const optionPatterns = [
      /^([A-Z])[\\.\\)\\s]+(.*)/,
      /^([a-z])[\\.\\)\\s]+(.*)/,
      /^•\\s*(.*)/,
      /^-\\s*(.*)/,
      /^\\*\\s*(.*)/];


      let optionMatch = null;
      for (const pattern of optionPatterns) {
        optionMatch = line.match(pattern);
        if (optionMatch) break;
      }

      if (optionMatch && currentQuestion.question) {
        if (!isInOptions) {
          isInOptions = true;
          currentQuestion.type = 'multiple_choice';
          currentQuestion.options = [];
          optionsList = [];
        }

        const optionText = optionMatch[2] || optionMatch[1];
        optionsList.push(optionText.trim());
        currentQuestion.options = [...optionsList];
        console.log(`🔸 Found option: ${optionText}`);
        continue;
      }

      // Look for true/false indicators
      if (line.toLowerCase().includes('true') && line.toLowerCase().includes('false')) {
        if (currentQuestion.question) {
          currentQuestion.type = 'true_false';
          currentQuestion.options = ['True', 'False'];
          console.log('✓ Detected True/False question');
        }
        continue;
      }

      // Look for answers - Enhanced patterns
      const answerPatterns = [
      /^(?:Answer|Correct\\s*Answer|Solution|Response)[\\:\\s]+(.*)/i,
      /^(?:A|Ans|Answer)[\\:\\s]+(.*)/i,
      /^Correct:\\s*(.*)/i,
      /^The\\s+(?:correct\\s+)?answer\\s+is[\\:\\s]*(.*)/i];


      let answerMatch = null;
      for (const pattern of answerPatterns) {
        answerMatch = line.match(pattern);
        if (answerMatch) break;
      }

      if (answerMatch && currentQuestion.question) {
        currentQuestion.correctAnswer = answerMatch[1].trim();
        console.log(`✓ Found answer: ${currentQuestion.correctAnswer}`);
        continue;
      }

      // Look for explanations
      const explanationPatterns = [
      /^(?:Explanation|Because|Reason|Note)[\\:\\s]+(.*)/i,
      /^Exp[\\:\\s]+(.*)/i];


      let explanationMatch = null;
      for (const pattern of explanationPatterns) {
        explanationMatch = line.match(pattern);
        if (explanationMatch) break;
      }

      if (explanationMatch && currentQuestion.question) {
        currentQuestion.explanation = explanationMatch[1].trim();
        console.log(`💡 Found explanation: ${currentQuestion.explanation.substring(0, 50)}`);
        continue;
      }

      // If we have a question and this line doesn't match any pattern, 
      // it might be a continuation of the question text
      if (currentQuestion.question && !currentQuestion.correctAnswer &&
      !line.includes(':') && !line.match(/^[A-Z][\\.\\)]/)) {
        currentQuestion.question += ' ' + line;
        console.log('📝 Extended question text');
      }
    }

    // Add the last question if it exists
    if (currentQuestion.question) {
      console.log('✅ Saving final question:', currentQuestion.question.substring(0, 50));
      questions.push({
        question: currentQuestion.question,
        correctAnswer: currentQuestion.correctAnswer || 'Answer not provided',
        type: currentQuestion.type || 'short_answer',
        explanation: currentQuestion.explanation,
        options: currentQuestion.options
      });
    }

    console.log(`🎯 Successfully parsed ${questions.length} questions from text format`);

    // Validate and clean up questions
    const validQuestions = questions.filter((q) =>
    q.question &&
    q.question.length > 10 &&
    q.correctAnswer &&
    q.correctAnswer !== 'Answer not provided'
    ).slice(0, quizData.numQuestions);

    console.log(`✨ Final valid questions count: ${validQuestions.length}`);

    return validQuestions;
  };

  const handleSaveQuiz = async () => {
    if (generatedQuestions.length === 0) {
      toast({
        title: "No Questions",
        description: "Please generate or select questions first.",
        variant: "destructive"
      });
      return;
    }

    try {
      const { data: userInfo, error: userError } = await window.ezsite.apis.getUserInfo();
      if (userError) throw userError;

      // Create quiz record
      const quizRecord = {
        teacher_id: userInfo.ID,
        class_id: quizData.classId && quizData.classId !== 'none' ? parseInt(quizData.classId) : 0,
        title: quizData.title,
        topic: quizData.topic,
        difficulty: quizData.difficulty,
        total_questions: generatedQuestions.length,
        time_limit: quizData.timeLimit
      };

      const { data: quizResult, error: quizError } = await window.ezsite.apis.tableCreate(QUIZZES_TABLE_ID, quizRecord);
      if (quizError) throw quizError;

      // Save quiz questions
      for (let i = 0; i < generatedQuestions.length; i++) {
        const question = generatedQuestions[i];
        const questionRecord = {
          quiz_id: quizResult,
          question_text: question.question,
          question_type: question.type,
          options: question.options ? JSON.stringify(question.options) : '',
          correct_answer: question.correctAnswer,
          points: 1,
          question_order: i + 1
        };

        const { error: questionError } = await window.ezsite.apis.tableCreate(QUIZ_QUESTIONS_TABLE_ID, questionRecord);
        if (questionError) throw questionError;
      }

      toast({
        title: "Quiz Saved",
        description: "Quiz has been saved successfully!"
      });

      // Reset form
      setGeneratedQuestions([]);
      setSelectedPoolQuestions([]);
      setQuizData((prev) => ({ ...prev, title: '', topic: '' }));

      // Refresh saved quizzes if on that tab
      if (activeTab === 'saved-quizzes') {
        fetchSavedQuizzes();
      }

    } catch (error) {
      console.error('Error saving quiz:', error);
      toast({
        title: "Save Failed",
        description: "Failed to save quiz. Please try again.",
        variant: "destructive"
      });
    }
  };

  const getQuestionTypeColor = (type: string) => {
    switch (type) {
      case 'multiple_choice':return 'bg-blue-100 text-blue-800';
      case 'true_false':return 'bg-green-100 text-green-800';
      case 'short_answer':return 'bg-yellow-100 text-yellow-800';
      case 'essay':return 'bg-purple-100 text-purple-800';
      default:return 'bg-gray-100 text-gray-800';
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy':return 'bg-green-100 text-green-800';
      case 'medium':return 'bg-yellow-100 text-yellow-800';
      case 'hard':return 'bg-red-100 text-red-800';
      default:return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6" data-id="rujauevv0" data-path="src/components/ai/QuizGenerator.tsx">
      <Card data-id="td0u6t1x4" data-path="src/components/ai/QuizGenerator.tsx">
        <CardHeader data-id="arrfh1awg" data-path="src/components/ai/QuizGenerator.tsx">
          <CardTitle className="flex items-center gap-2" data-id="vh6dneg70" data-path="src/components/ai/QuizGenerator.tsx">
            <Brain className="h-5 w-5" data-id="lp9esrnou" data-path="src/components/ai/QuizGenerator.tsx" />
            Enhanced Quiz Generator
          </CardTitle>
          <CardDescription data-id="36hf7jck4" data-path="src/components/ai/QuizGenerator.tsx">
            Generate quizzes using AI, from resources (PDF, images, web links), create them from your questions pool, or manage your saved quizzes. Export questions and answer keys separately.
          </CardDescription>
        </CardHeader>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6" data-id="xthax8sv8" data-path="src/components/ai/QuizGenerator.tsx">
        <TabsList className="grid w-full grid-cols-4" data-id="gppd1hg8l" data-path="src/components/ai/QuizGenerator.tsx">
          <TabsTrigger value="ai-generate" className="flex items-center gap-2" data-id="udnc39e7d" data-path="src/components/ai/QuizGenerator.tsx">
            <Wand2 className="h-4 w-4" data-id="khsppj69c" data-path="src/components/ai/QuizGenerator.tsx" />
            AI Generate
          </TabsTrigger>
          <TabsTrigger value="from-resources" className="flex items-center gap-2" data-id="d7bv7vi8u" data-path="src/components/ai/QuizGenerator.tsx">
            <Sparkles className="h-4 w-4" data-id="rd9q7ivje" data-path="src/components/ai/QuizGenerator.tsx" />
            From Resources
          </TabsTrigger>
          <TabsTrigger value="from-pool" className="flex items-center gap-2" data-id="p94c4ei7b" data-path="src/components/ai/QuizGenerator.tsx">
            <BookOpen className="h-4 w-4" data-id="ey7b4ltwj" data-path="src/components/ai/QuizGenerator.tsx" />
            From Questions Pool
          </TabsTrigger>
          <TabsTrigger value="saved-quizzes" className="flex items-center gap-2" data-id="sowhu8x7f" data-path="src/components/ai/QuizGenerator.tsx">
            <FileText className="h-4 w-4" data-id="tgyv5388j" data-path="src/components/ai/QuizGenerator.tsx" />
            Saved Quizzes ({savedQuizzes.length})
          </TabsTrigger>
        </TabsList>

        {/* Quiz Settings (Common for generation tabs) */}
        {(activeTab === 'ai-generate' || activeTab === 'from-resources' || activeTab === 'from-pool') &&
        <Card data-id="v0008nv96" data-path="src/components/ai/QuizGenerator.tsx">
            <CardHeader data-id="6wuvlyfak" data-path="src/components/ai/QuizGenerator.tsx">
              <CardTitle className="flex items-center gap-2" data-id="a7u80ee69" data-path="src/components/ai/QuizGenerator.tsx">
                <Target className="h-5 w-5" data-id="lqajvl516" data-path="src/components/ai/QuizGenerator.tsx" />
                Quiz Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4" data-id="5lhw4jaq6" data-path="src/components/ai/QuizGenerator.tsx">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="ijd85pskl" data-path="src/components/ai/QuizGenerator.tsx">
                <div className="space-y-2" data-id="xi0rqetk4" data-path="src/components/ai/QuizGenerator.tsx">
                  <Label htmlFor="title" data-id="wq2aww8fn" data-path="src/components/ai/QuizGenerator.tsx">Quiz Title *</Label>
                  <Input
                  id="title"
                  value={quizData.title}
                  onChange={(e) => setQuizData({ ...quizData, title: e.target.value })}
                  placeholder="Enter quiz title" data-id="mlx8v52d8" data-path="src/components/ai/QuizGenerator.tsx" />

                </div>
                <div className="space-y-2" data-id="p1z8uds7i" data-path="src/components/ai/QuizGenerator.tsx">
                  <Label htmlFor="topic" data-id="vbdikkibm" data-path="src/components/ai/QuizGenerator.tsx">Topic *</Label>
                  <Input
                  id="topic"
                  value={quizData.topic}
                  onChange={(e) => setQuizData({ ...quizData, topic: e.target.value })}
                  placeholder="Enter quiz topic" data-id="5zfwtozup" data-path="src/components/ai/QuizGenerator.tsx" />

                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="4tbopy2h6" data-path="src/components/ai/QuizGenerator.tsx">
                <div className="space-y-2" data-id="qixqssfp9" data-path="src/components/ai/QuizGenerator.tsx">
                  <Label htmlFor="difficulty" data-id="yf2gnxand" data-path="src/components/ai/QuizGenerator.tsx">Difficulty</Label>
                  <Select
                  value={quizData.difficulty}
                  onValueChange={(value: 'easy' | 'medium' | 'hard') =>
                  setQuizData({ ...quizData, difficulty: value })
                  } data-id="p4d94wxz2" data-path="src/components/ai/QuizGenerator.tsx">

                    <SelectTrigger data-id="mzwhciv2n" data-path="src/components/ai/QuizGenerator.tsx">
                      <SelectValue data-id="j86twvqok" data-path="src/components/ai/QuizGenerator.tsx" />
                    </SelectTrigger>
                    <SelectContent data-id="m3h1pi829" data-path="src/components/ai/QuizGenerator.tsx">
                      <SelectItem value="easy" data-id="vmn46dq77" data-path="src/components/ai/QuizGenerator.tsx">Easy</SelectItem>
                      <SelectItem value="medium" data-id="cb85pk4ny" data-path="src/components/ai/QuizGenerator.tsx">Medium</SelectItem>
                      <SelectItem value="hard" data-id="q1p9vkjh2" data-path="src/components/ai/QuizGenerator.tsx">Hard</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2" data-id="ypvhzvu9i" data-path="src/components/ai/QuizGenerator.tsx">
                  <Label htmlFor="timeLimit" data-id="9hpeovflv" data-path="src/components/ai/QuizGenerator.tsx">Time Limit (minutes)</Label>
                  <Input
                  id="timeLimit"
                  type="number"
                  value={quizData.timeLimit}
                  onChange={(e) => setQuizData({ ...quizData, timeLimit: parseInt(e.target.value) || 30 })}
                  min="1"
                  max="180" data-id="twoxwpb6r" data-path="src/components/ai/QuizGenerator.tsx" />

                </div>

                <div className="space-y-2" data-id="mmwrmc4vm" data-path="src/components/ai/QuizGenerator.tsx">
                  <Label htmlFor="class" data-id="o76e6zg70" data-path="src/components/ai/QuizGenerator.tsx">Class (Optional)</Label>
                  <Select
                  value={quizData.classId}
                  onValueChange={(value) => setQuizData({ ...quizData, classId: value })} data-id="ezkcs0h76" data-path="src/components/ai/QuizGenerator.tsx">

                    <SelectTrigger data-id="8xrkq8vam" data-path="src/components/ai/QuizGenerator.tsx">
                      <SelectValue placeholder="Select class" data-id="4rjugarsr" data-path="src/components/ai/QuizGenerator.tsx" />
                    </SelectTrigger>
                    <SelectContent data-id="6o9s1t4i1" data-path="src/components/ai/QuizGenerator.tsx">
                      <SelectItem value="none" data-id="npjjdl2nn" data-path="src/components/ai/QuizGenerator.tsx">No class</SelectItem>
                      {classes.map((cls) =>
                    <SelectItem key={cls.id} value={cls.id.toString()} data-id="72lwidaug" data-path="src/components/ai/QuizGenerator.tsx">
                          {cls.class_name}
                        </SelectItem>
                    )}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
        }

        <TabsContent value="ai-generate" className="space-y-6" data-id="tpyj8wjvv" data-path="src/components/ai/QuizGenerator.tsx">
          <Card data-id="bi1er195f" data-path="src/components/ai/QuizGenerator.tsx">
            <CardHeader data-id="k7766er6w" data-path="src/components/ai/QuizGenerator.tsx">
              <CardTitle data-id="ht7bl9ohs" data-path="src/components/ai/QuizGenerator.tsx">AI Generation Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4" data-id="f9o0zjyw9" data-path="src/components/ai/QuizGenerator.tsx">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="q918pr9f5" data-path="src/components/ai/QuizGenerator.tsx">
                <div className="space-y-2" data-id="x5cfe7gu4" data-path="src/components/ai/QuizGenerator.tsx">
                  <Label htmlFor="gradeLevel" data-id="6qqminz9y" data-path="src/components/ai/QuizGenerator.tsx">Grade Level</Label>
                  <Input
                    id="gradeLevel"
                    value={quizData.gradeLevel}
                    onChange={(e) => setQuizData({ ...quizData, gradeLevel: e.target.value })}
                    placeholder="e.g., 8th grade, High School" data-id="zz6q22z9u" data-path="src/components/ai/QuizGenerator.tsx" />

                </div>
                <div className="space-y-2" data-id="qlthygcf9" data-path="src/components/ai/QuizGenerator.tsx">
                  <Label htmlFor="numQuestions" data-id="b8d4a4jog" data-path="src/components/ai/QuizGenerator.tsx">Number of Questions</Label>
                  <Input
                    id="numQuestions"
                    type="number"
                    value={quizData.numQuestions}
                    onChange={(e) => setQuizData({ ...quizData, numQuestions: parseInt(e.target.value) || 10 })}
                    min="1"
                    max="50" data-id="jjtzphof0" data-path="src/components/ai/QuizGenerator.tsx" />

                </div>
              </div>

              {isGenerating &&
              <div className="space-y-2" data-id="w4m32w32e" data-path="src/components/ai/QuizGenerator.tsx">
                  <div className="flex justify-between text-sm" data-id="37gm9o663" data-path="src/components/ai/QuizGenerator.tsx">
                    <span data-id="m2nmnonwf" data-path="src/components/ai/QuizGenerator.tsx">Generating quiz questions...</span>
                    <span data-id="mssutj1zr" data-path="src/components/ai/QuizGenerator.tsx">{generationProgress}%</span>
                  </div>
                  <Progress value={generationProgress} className="w-full" data-id="msqf0g1tx" data-path="src/components/ai/QuizGenerator.tsx" />
                </div>
              }

              <Button
                onClick={handleGenerateQuiz}
                disabled={isGenerating}
                className="w-full" data-id="21u4c9geq" data-path="src/components/ai/QuizGenerator.tsx">

                {isGenerating ?
                <>
                    <Wand2 className="h-4 w-4 mr-2 animate-spin" data-id="vd086vcjx" data-path="src/components/ai/QuizGenerator.tsx" />
                    Generating Quiz...
                  </> :
                <>
                    <Wand2 className="h-4 w-4 mr-2" data-id="rblmrtnwc" data-path="src/components/ai/QuizGenerator.tsx" />
                    Generate Quiz with AI
                  </>
                }
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="from-resources" className="space-y-6" data-id="d3w8fljh0" data-path="src/components/ai/QuizGenerator.tsx">
          <ResourceBasedQuizGenerator
            onQuestionsGenerated={handleResourceQuestionsGenerated}
            quizTitle={quizData.title}
            quizTopic={quizData.topic}
            numQuestions={quizData.numQuestions}
            difficulty={quizData.difficulty} data-id="jbdzaavd0" data-path="src/components/ai/QuizGenerator.tsx" />

        </TabsContent>

        <TabsContent value="from-pool" className="space-y-6" data-id="9dkpczrms" data-path="src/components/ai/QuizGenerator.tsx">
          <Card data-id="md4vk08vr" data-path="src/components/ai/QuizGenerator.tsx">
            <CardHeader data-id="knm4mhimm" data-path="src/components/ai/QuizGenerator.tsx">
              <CardTitle data-id="s923tf5c5" data-path="src/components/ai/QuizGenerator.tsx">Questions Pool Selection</CardTitle>
              <CardDescription data-id="qu7iba1yy" data-path="src/components/ai/QuizGenerator.tsx">
                Select questions from your existing pool with advanced filtering options
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4" data-id="wpvhuuri8" data-path="src/components/ai/QuizGenerator.tsx">
              {/* Filter Controls */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="4vgt6cih0" data-path="src/components/ai/QuizGenerator.tsx">
                <div className="space-y-2" data-id="a42b9v0og" data-path="src/components/ai/QuizGenerator.tsx">
                  <Label data-id="q42u41rhr" data-path="src/components/ai/QuizGenerator.tsx">Pre-filter Category</Label>
                  <Select value={poolFilters.category} onValueChange={(value) => setPoolFilters({ ...poolFilters, category: value })} data-id="9och4tfo8" data-path="src/components/ai/QuizGenerator.tsx">
                    <SelectTrigger data-id="z7cyqpi07" data-path="src/components/ai/QuizGenerator.tsx">
                      <SelectValue placeholder="Any category" data-id="7rd72d1t1" data-path="src/components/ai/QuizGenerator.tsx" />
                    </SelectTrigger>
                    <SelectContent data-id="m7n54g5qs" data-path="src/components/ai/QuizGenerator.tsx">
                      <SelectItem value="all" data-id="yy3ggiwks" data-path="src/components/ai/QuizGenerator.tsx">Any category</SelectItem>
                      <SelectItem value="Mathematics" data-id="war3buj4t" data-path="src/components/ai/QuizGenerator.tsx">Mathematics</SelectItem>
                      <SelectItem value="Science" data-id="92hgi6sme" data-path="src/components/ai/QuizGenerator.tsx">Science</SelectItem>
                      <SelectItem value="History" data-id="yo439nnt8" data-path="src/components/ai/QuizGenerator.tsx">History</SelectItem>
                      <SelectItem value="English" data-id="phuk1qy2h" data-path="src/components/ai/QuizGenerator.tsx">English</SelectItem>
                      <SelectItem value="Geography" data-id="9p2x2f9s0" data-path="src/components/ai/QuizGenerator.tsx">Geography</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2" data-id="l1m4kdd39" data-path="src/components/ai/QuizGenerator.tsx">
                  <Label data-id="puip1vvqx" data-path="src/components/ai/QuizGenerator.tsx">Pre-filter Difficulty</Label>
                  <Select value={poolFilters.difficulty} onValueChange={(value) => setPoolFilters({ ...poolFilters, difficulty: value })} data-id="715219wcv" data-path="src/components/ai/QuizGenerator.tsx">
                    <SelectTrigger data-id="n1kftyxsv" data-path="src/components/ai/QuizGenerator.tsx">
                      <SelectValue placeholder="Any difficulty" data-id="ztimt7qcn" data-path="src/components/ai/QuizGenerator.tsx" />
                    </SelectTrigger>
                    <SelectContent data-id="lqr74rb46" data-path="src/components/ai/QuizGenerator.tsx">
                      <SelectItem value="all" data-id="f1rex4ch7" data-path="src/components/ai/QuizGenerator.tsx">Any difficulty</SelectItem>
                      <SelectItem value="easy" data-id="fuuibp8ok" data-path="src/components/ai/QuizGenerator.tsx">Easy</SelectItem>
                      <SelectItem value="medium" data-id="20mrzi2ga" data-path="src/components/ai/QuizGenerator.tsx">Medium</SelectItem>
                      <SelectItem value="hard" data-id="t81v5hnyc" data-path="src/components/ai/QuizGenerator.tsx">Hard</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2" data-id="xdj9ybt0z" data-path="src/components/ai/QuizGenerator.tsx">
                  <Label data-id="1oc7fjgwg" data-path="src/components/ai/QuizGenerator.tsx">Tags Filter</Label>
                  <Input
                    placeholder="Filter by tags"
                    value={poolFilters.tags}
                    onChange={(e) => setPoolFilters({ ...poolFilters, tags: e.target.value })} data-id="rukxq6v57" data-path="src/components/ai/QuizGenerator.tsx" />

                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2" data-id="c9pis1yw1" data-path="src/components/ai/QuizGenerator.tsx">
                <Dialog open={isQuestionSelectorOpen} onOpenChange={setIsQuestionSelectorOpen} data-id="4iy5bpcq3" data-path="src/components/ai/QuizGenerator.tsx">
                  <DialogTrigger asChild data-id="d68i8kj53" data-path="src/components/ai/QuizGenerator.tsx">
                    <Button data-id="26nvh1dw8" data-path="src/components/ai/QuizGenerator.tsx">
                      <Search className="h-4 w-4 mr-2" data-id="kt4m68sc4" data-path="src/components/ai/QuizGenerator.tsx" />
                      Select Questions from Pool
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto" data-id="n2ni16kdk" data-path="src/components/ai/QuizGenerator.tsx">
                    <DialogHeader data-id="rrme5yfcp" data-path="src/components/ai/QuizGenerator.tsx">
                      <DialogTitle data-id="jitxbz0mh" data-path="src/components/ai/QuizGenerator.tsx">Select Questions</DialogTitle>
                      <DialogDescription data-id="26vfqowkq" data-path="src/components/ai/QuizGenerator.tsx">
                        Choose questions from your pool with advanced filtering
                      </DialogDescription>
                    </DialogHeader>
                    <QuestionSelector
                      onQuestionsSelected={handleQuestionsSelected}
                      maxQuestions={50}
                      prefilterCategory={poolFilters.category === 'all' ? '' : poolFilters.category}
                      prefilterDifficulty={poolFilters.difficulty === 'all' ? '' : poolFilters.difficulty} data-id="dulcdywf7" data-path="src/components/ai/QuizGenerator.tsx" />

                  </DialogContent>
                </Dialog>

                {selectedPoolQuestions.length > 0 &&
                <Button onClick={handleCreateFromPool} variant="outline" data-id="w3a3tbdry" data-path="src/components/ai/QuizGenerator.tsx">
                    <Plus className="h-4 w-4 mr-2" data-id="radqtu0xf" data-path="src/components/ai/QuizGenerator.tsx" />
                    Create Quiz ({selectedPoolQuestions.length} questions)
                  </Button>
                }
              </div>

              {/* Selected Questions Summary */}
              {selectedPoolQuestions.length > 0 &&
              <Card data-id="oun61kbuz" data-path="src/components/ai/QuizGenerator.tsx">
                  <CardHeader data-id="ze4xckgc9" data-path="src/components/ai/QuizGenerator.tsx">
                    <CardTitle data-id="6ib46as9o" data-path="src/components/ai/QuizGenerator.tsx">Selected Questions ({selectedPoolQuestions.length})</CardTitle>
                  </CardHeader>
                  <CardContent data-id="0d2kob7fg" data-path="src/components/ai/QuizGenerator.tsx">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm" data-id="xcrxyke12" data-path="src/components/ai/QuizGenerator.tsx">
                      <div data-id="8crble08e" data-path="src/components/ai/QuizGenerator.tsx">
                        <strong data-id="t5bi8mxoj" data-path="src/components/ai/QuizGenerator.tsx">Categories:</strong>
                        <div className="flex flex-wrap gap-1 mt-1" data-id="4glyjvp4c" data-path="src/components/ai/QuizGenerator.tsx">
                          {[...new Set(selectedPoolQuestions.map((q) => q.subject))].map((subject) =>
                        <Badge key={subject} variant="outline" className="text-xs" data-id="gmpco8iea" data-path="src/components/ai/QuizGenerator.tsx">
                              {subject}
                            </Badge>
                        )}
                        </div>
                      </div>
                      <div data-id="6q2yb9n9n" data-path="src/components/ai/QuizGenerator.tsx">
                        <strong data-id="xsey7925e" data-path="src/components/ai/QuizGenerator.tsx">Difficulties:</strong>
                        <div className="flex flex-wrap gap-1 mt-1" data-id="l6s2u1b3g" data-path="src/components/ai/QuizGenerator.tsx">
                          {[...new Set(selectedPoolQuestions.map((q) => q.difficulty))].map((difficulty) =>
                        <Badge key={difficulty} variant="secondary" className="text-xs" data-id="ikzhupilm" data-path="src/components/ai/QuizGenerator.tsx">
                              {difficulty}
                            </Badge>
                        )}
                        </div>
                      </div>
                      <div data-id="sxax2rsc0" data-path="src/components/ai/QuizGenerator.tsx">
                        <strong data-id="gnhp56ixk" data-path="src/components/ai/QuizGenerator.tsx">Types:</strong>
                        <div className="flex flex-wrap gap-1 mt-1" data-id="n049i4722" data-path="src/components/ai/QuizGenerator.tsx">
                          {[...new Set(selectedPoolQuestions.map((q) => q.question_type))].map((type) =>
                        <Badge key={type} className="text-xs" data-id="i6a8uozcm" data-path="src/components/ai/QuizGenerator.tsx">
                              {type.replace('_', ' ')}
                            </Badge>
                        )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              }
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="saved-quizzes" className="space-y-6" data-id="p3vp51z39" data-path="src/components/ai/QuizGenerator.tsx">
          <Card data-id="rp0zdjog4" data-path="src/components/ai/QuizGenerator.tsx">
            <CardHeader data-id="c5gtvtba2" data-path="src/components/ai/QuizGenerator.tsx">
              <CardTitle className="flex items-center justify-between" data-id="0eu2sqmhj" data-path="src/components/ai/QuizGenerator.tsx">
                <span data-id="ontly9xcw" data-path="src/components/ai/QuizGenerator.tsx">Saved Quizzes</span>
                <Badge variant="secondary" data-id="my344iouc" data-path="src/components/ai/QuizGenerator.tsx">{savedQuizzes.length} quizzes</Badge>
              </CardTitle>
              <CardDescription data-id="jssclolf4" data-path="src/components/ai/QuizGenerator.tsx">
                Manage your saved quizzes - view, edit, or delete them. Download questions and answer keys separately.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4" data-id="e11wb4xnz" data-path="src/components/ai/QuizGenerator.tsx">
              {/* Filters */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="808naplwc" data-path="src/components/ai/QuizGenerator.tsx">
                <div className="space-y-2" data-id="6sjxr6oub" data-path="src/components/ai/QuizGenerator.tsx">
                  <Label htmlFor="search" data-id="xq7s8gsn6" data-path="src/components/ai/QuizGenerator.tsx">Search</Label>
                  <Input
                    id="search"
                    placeholder="Search by title..."
                    value={savedQuizFilters.search}
                    onChange={(e) => setSavedQuizFilters({ ...savedQuizFilters, search: e.target.value })} data-id="66rxttopn" data-path="src/components/ai/QuizGenerator.tsx" />

                </div>
                <div className="space-y-2" data-id="0s4va48hb" data-path="src/components/ai/QuizGenerator.tsx">
                  <Label data-id="munwmblba" data-path="src/components/ai/QuizGenerator.tsx">Difficulty</Label>
                  <Select
                    value={savedQuizFilters.difficulty}
                    onValueChange={(value) => setSavedQuizFilters({ ...savedQuizFilters, difficulty: value })} data-id="888ujxy4f" data-path="src/components/ai/QuizGenerator.tsx">

                    <SelectTrigger data-id="49c41hcrc" data-path="src/components/ai/QuizGenerator.tsx">
                      <SelectValue data-id="884god5dr" data-path="src/components/ai/QuizGenerator.tsx" />
                    </SelectTrigger>
                    <SelectContent data-id="diz3h7qm3" data-path="src/components/ai/QuizGenerator.tsx">
                      <SelectItem value="all" data-id="x1zyldetw" data-path="src/components/ai/QuizGenerator.tsx">All difficulties</SelectItem>
                      <SelectItem value="easy" data-id="3q3tc8mu0" data-path="src/components/ai/QuizGenerator.tsx">Easy</SelectItem>
                      <SelectItem value="medium" data-id="we6juxtco" data-path="src/components/ai/QuizGenerator.tsx">Medium</SelectItem>
                      <SelectItem value="hard" data-id="hl8n4j76n" data-path="src/components/ai/QuizGenerator.tsx">Hard</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2" data-id="9w9c5t3d5" data-path="src/components/ai/QuizGenerator.tsx">
                  <Label data-id="1jtza71az" data-path="src/components/ai/QuizGenerator.tsx">Class</Label>
                  <Select
                    value={savedQuizFilters.class}
                    onValueChange={(value) => setSavedQuizFilters({ ...savedQuizFilters, class: value })} data-id="lmoekxu3y" data-path="src/components/ai/QuizGenerator.tsx">

                    <SelectTrigger data-id="6uq0kgnob" data-path="src/components/ai/QuizGenerator.tsx">
                      <SelectValue data-id="ucrzucowl" data-path="src/components/ai/QuizGenerator.tsx" />
                    </SelectTrigger>
                    <SelectContent data-id="bjwww8bb5" data-path="src/components/ai/QuizGenerator.tsx">
                      <SelectItem value="all" data-id="yajancm8k" data-path="src/components/ai/QuizGenerator.tsx">All classes</SelectItem>
                      <SelectItem value="0" data-id="ofs8m5kn3" data-path="src/components/ai/QuizGenerator.tsx">No class</SelectItem>
                      {classes.map((cls) =>
                      <SelectItem key={cls.id} value={cls.id.toString()} data-id="nvqop9j7b" data-path="src/components/ai/QuizGenerator.tsx">
                          {cls.class_name}
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Button onClick={fetchSavedQuizzes} variant="outline" size="sm" data-id="nzbdgww46" data-path="src/components/ai/QuizGenerator.tsx">
                <Search className="h-4 w-4 mr-2" data-id="9g4l82rwp" data-path="src/components/ai/QuizGenerator.tsx" />
                Apply Filters
              </Button>

              {/* Quiz List */}
              {isLoadingSavedQuizzes ?
              <div className="flex items-center justify-center p-8" data-id="o9lptowbw" data-path="src/components/ai/QuizGenerator.tsx">
                  <div className="text-center space-y-2" data-id="a43dc5ebg" data-path="src/components/ai/QuizGenerator.tsx">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto" data-id="o472imggk" data-path="src/components/ai/QuizGenerator.tsx"></div>
                    <p className="text-sm text-gray-500" data-id="vwo1g99vz" data-path="src/components/ai/QuizGenerator.tsx">Loading saved quizzes...</p>
                  </div>
                </div> :
              savedQuizzes.length === 0 ?
              <div className="text-center p-8" data-id="fpbkdjam7" data-path="src/components/ai/QuizGenerator.tsx">
                    <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" data-id="gvrt6u71u" data-path="src/components/ai/QuizGenerator.tsx" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2" data-id="qkogxu0df" data-path="src/components/ai/QuizGenerator.tsx">No quizzes found</h3>
                    <p className="text-gray-500 mb-4" data-id="37pybej1j" data-path="src/components/ai/QuizGenerator.tsx">
                      {savedQuizFilters.search || savedQuizFilters.difficulty !== 'all' || savedQuizFilters.class !== 'all' ?
                  "No quizzes match your current filters." :
                  "You haven't created any quizzes yet."}
                    </p>
                    <Button onClick={() => setActiveTab('ai-generate')} data-id="m04jikg7r" data-path="src/components/ai/QuizGenerator.tsx">
                      <Plus className="h-4 w-4 mr-2" data-id="yg2yrfzaj" data-path="src/components/ai/QuizGenerator.tsx" />
                      Create Your First Quiz
                    </Button>
                  </div> :
              <div className="grid gap-4" data-id="c46atgkkc" data-path="src/components/ai/QuizGenerator.tsx">
                    {savedQuizzes.map((quiz) =>
                <Card key={quiz.id} className="hover:shadow-md transition-shadow" data-id="7xngnzao3" data-path="src/components/ai/QuizGenerator.tsx">
                        <CardContent className="p-6" data-id="1ua0rb3hi" data-path="src/components/ai/QuizGenerator.tsx">
                          <div className="flex items-start justify-between" data-id="x9mz6wyrd" data-path="src/components/ai/QuizGenerator.tsx">
                            <div className="flex-1 space-y-2" data-id="a3cwglnpw" data-path="src/components/ai/QuizGenerator.tsx">
                              <div className="flex items-center gap-2" data-id="udh7nyeps" data-path="src/components/ai/QuizGenerator.tsx">
                                <h3 className="font-semibold text-lg" data-id="53x8l4kav" data-path="src/components/ai/QuizGenerator.tsx">{quiz.title}</h3>
                              </div>

                              <p className="text-sm text-gray-600" data-id="2uzibhuho" data-path="src/components/ai/QuizGenerator.tsx">{quiz.topic}</p>

                              <div className="flex items-center gap-4 text-sm text-gray-500" data-id="gbuzcg3le" data-path="src/components/ai/QuizGenerator.tsx">
                                <div className="flex items-center gap-1" data-id="sfmsluip5" data-path="src/components/ai/QuizGenerator.tsx">
                                  <Target className="h-4 w-4" data-id="hdkt6gwv6" data-path="src/components/ai/QuizGenerator.tsx" />
                                  <Badge className={getDifficultyColor(quiz.difficulty)} variant="outline" data-id="j55be3kyw" data-path="src/components/ai/QuizGenerator.tsx">
                                    {quiz.difficulty}
                                  </Badge>
                                </div>
                                <div className="flex items-center gap-1" data-id="3oftdf738" data-path="src/components/ai/QuizGenerator.tsx">
                                  <FileText className="h-4 w-4" data-id="jj46s8u1i" data-path="src/components/ai/QuizGenerator.tsx" />
                                  {quiz.total_questions} questions
                                </div>
                                <div className="flex items-center gap-1" data-id="o8mrzz5nb" data-path="src/components/ai/QuizGenerator.tsx">
                                  <Clock className="h-4 w-4" data-id="wg0rvorqn" data-path="src/components/ai/QuizGenerator.tsx" />
                                  {quiz.time_limit} min
                                </div>
                                {quiz.class_name && quiz.class_name !== 'No Class' &&
                          <div className="flex items-center gap-1" data-id="m4vuearz8" data-path="src/components/ai/QuizGenerator.tsx">
                                    <Users className="h-4 w-4" data-id="4prttz33z" data-path="src/components/ai/QuizGenerator.tsx" />
                                    {quiz.class_name}
                                  </div>
                          }
                              </div>

                              {quiz.created_at &&
                        <p className="text-xs text-gray-400" data-id="9g6kgcnnd" data-path="src/components/ai/QuizGenerator.tsx">
                                  Created {formatDate(quiz.created_at)}
                                </p>
                        }
                            </div>

                            <div className="flex items-center gap-2" data-id="ptgml56at" data-path="src/components/ai/QuizGenerator.tsx">
                              <Button
                          variant="outline"
                          size="sm"
                          onClick={() => fetchQuizDetails(quiz.id)} data-id="6em0nsx8q" data-path="src/components/ai/QuizGenerator.tsx">

                                <Eye className="h-4 w-4" data-id="jm8zcwv2g" data-path="src/components/ai/QuizGenerator.tsx" />
                              </Button>

                              <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditQuiz(quiz)} data-id="z3bdhzasv" data-path="src/components/ai/QuizGenerator.tsx">

                                <Edit className="h-4 w-4" data-id="2kpnlgelh" data-path="src/components/ai/QuizGenerator.tsx" />
                              </Button>

                              <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownloadSavedQuizQuestions(quiz)} data-id="t8y1y67f7" data-path="src/components/ai/QuizGenerator.tsx">

                                <Download className="h-4 w-4" data-id="9svmxjlwt" data-path="src/components/ai/QuizGenerator.tsx" />
                                Questions
                              </Button>

                              <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownloadSavedQuizAnswerKey(quiz)} data-id="5stgd2o1g" data-path="src/components/ai/QuizGenerator.tsx">

                                <FileDown className="h-4 w-4" data-id="jt4fkfl2v" data-path="src/components/ai/QuizGenerator.tsx" />
                                Answer Key
                              </Button>

                              <AlertDialog data-id="9nmi3xuyb" data-path="src/components/ai/QuizGenerator.tsx">
                                <AlertDialogTrigger asChild data-id="anafro9xc" data-path="src/components/ai/QuizGenerator.tsx">
                                  <Button variant="destructive" size="sm" data-id="h02lrzcye" data-path="src/components/ai/QuizGenerator.tsx">
                                    <Trash2 className="h-4 w-4" data-id="wl64tmdfy" data-path="src/components/ai/QuizGenerator.tsx" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent data-id="opfbc48je" data-path="src/components/ai/QuizGenerator.tsx">
                                  <AlertDialogHeader data-id="o0u3bytgz" data-path="src/components/ai/QuizGenerator.tsx">
                                    <AlertDialogTitle data-id="qfrnrs224" data-path="src/components/ai/QuizGenerator.tsx">Delete Quiz</AlertDialogTitle>
                                    <AlertDialogDescription data-id="92fnmkfql" data-path="src/components/ai/QuizGenerator.tsx">
                                      Are you sure you want to delete "{quiz.title}"? This action cannot be undone.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter data-id="su2yfkuse" data-path="src/components/ai/QuizGenerator.tsx">
                                    <AlertDialogCancel data-id="43y2rqiq1" data-path="src/components/ai/QuizGenerator.tsx">Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                onClick={() => handleDeleteQuiz(quiz.id)}
                                className="bg-red-600 hover:bg-red-700" data-id="gzdge74qn" data-path="src/components/ai/QuizGenerator.tsx">

                                      Delete
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                )}
                  </div>
              }
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Quiz Detail Dialog */}
      <Dialog open={isQuizDetailOpen} onOpenChange={setIsQuizDetailOpen} data-id="xecwfenfk" data-path="src/components/ai/QuizGenerator.tsx">
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-id="kh2y7ts2j" data-path="src/components/ai/QuizGenerator.tsx">
          <DialogHeader data-id="f5w6rmp70" data-path="src/components/ai/QuizGenerator.tsx">
            <DialogTitle className="flex items-center gap-2" data-id="b6p3dixyp" data-path="src/components/ai/QuizGenerator.tsx">
              <Eye className="h-5 w-5" data-id="rusk56yxe" data-path="src/components/ai/QuizGenerator.tsx" />
              {quizDetailView?.title}
            </DialogTitle>
            <DialogDescription data-id="hz7hel23m" data-path="src/components/ai/QuizGenerator.tsx">
              View detailed quiz content and questions
            </DialogDescription>
          </DialogHeader>

          {quizDetailView &&
          <div className="space-y-6" data-id="abne1eib6" data-path="src/components/ai/QuizGenerator.tsx">
              {/* Quiz Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm" data-id="gbpjiusec" data-path="src/components/ai/QuizGenerator.tsx">
                <div data-id="xh5dystiu" data-path="src/components/ai/QuizGenerator.tsx">
                  <Label className="font-medium" data-id="xxix80fh2" data-path="src/components/ai/QuizGenerator.tsx">Topic</Label>
                  <p className="text-gray-600" data-id="7uua9odzf" data-path="src/components/ai/QuizGenerator.tsx">{quizDetailView.topic}</p>
                </div>
                <div data-id="4zn8671sv" data-path="src/components/ai/QuizGenerator.tsx">
                  <Label className="font-medium" data-id="qd1uzr5o4" data-path="src/components/ai/QuizGenerator.tsx">Difficulty</Label>
                  <Badge className={getDifficultyColor(quizDetailView.difficulty)} variant="outline" data-id="pinyvijlo" data-path="src/components/ai/QuizGenerator.tsx">
                    {quizDetailView.difficulty}
                  </Badge>
                </div>
                <div data-id="5yad24hub" data-path="src/components/ai/QuizGenerator.tsx">
                  <Label className="font-medium" data-id="bc5gywnbv" data-path="src/components/ai/QuizGenerator.tsx">Time Limit</Label>
                  <p className="text-gray-600" data-id="vo3que96x" data-path="src/components/ai/QuizGenerator.tsx">{quizDetailView.time_limit} minutes</p>
                </div>
                <div data-id="9jjer0hbt" data-path="src/components/ai/QuizGenerator.tsx">
                  <Label className="font-medium" data-id="assvb4az8" data-path="src/components/ai/QuizGenerator.tsx">Questions</Label>
                  <p className="text-gray-600" data-id="3p1ghhj0t" data-path="src/components/ai/QuizGenerator.tsx">{quizDetailView.questions?.length || 0}</p>
                </div>
              </div>

              <Separator data-id="0obf7jkyp" data-path="src/components/ai/QuizGenerator.tsx" />

              {/* Questions */}
              <div className="space-y-4" data-id="qjep2nzl1" data-path="src/components/ai/QuizGenerator.tsx">
                <h3 className="font-semibold" data-id="fwjkgkvi2" data-path="src/components/ai/QuizGenerator.tsx">Questions ({quizDetailView.questions?.length || 0})</h3>
                {quizDetailView.questions?.map((question, index) =>
              <Card key={question.id} data-id="vb19u1onj" data-path="src/components/ai/QuizGenerator.tsx">
                    <CardContent className="pt-4" data-id="qkvji9r5l" data-path="src/components/ai/QuizGenerator.tsx">
                      <div className="space-y-3" data-id="gqyr6ra0m" data-path="src/components/ai/QuizGenerator.tsx">
                        <div className="flex items-start justify-between" data-id="3ci1428a1" data-path="src/components/ai/QuizGenerator.tsx">
                          <h4 className="font-medium" data-id="5fzhj6nzr" data-path="src/components/ai/QuizGenerator.tsx">Question {index + 1}:</h4>
                          <Badge className={getQuestionTypeColor(question.question_type)} data-id="2xpcqngb9" data-path="src/components/ai/QuizGenerator.tsx">
                            {question.question_type.replace('_', ' ')}
                          </Badge>
                        </div>
                        <p data-id="d0xzyf1g0" data-path="src/components/ai/QuizGenerator.tsx">{question.question_text}</p>

                        {question.options &&
                    <div data-id="uyf0hoa9p" data-path="src/components/ai/QuizGenerator.tsx">
                            <p className="font-medium" data-id="xhvryjft2" data-path="src/components/ai/QuizGenerator.tsx">Options:</p>
                            <ul className="list-disc list-inside" data-id="izurju852" data-path="src/components/ai/QuizGenerator.tsx">
                              {JSON.parse(question.options).map((option: string, optIndex: number) =>
                        <li key={optIndex} data-id="asd2b6axi" data-path="src/components/ai/QuizGenerator.tsx">{option}</li>
                        )}
                            </ul>
                          </div>
                    }

                        <div data-id="kq7kmjl4g" data-path="src/components/ai/QuizGenerator.tsx">
                          <p className="font-medium" data-id="nfqc79060" data-path="src/components/ai/QuizGenerator.tsx">Correct Answer:</p>
                          <p className="text-green-700" data-id="tr0zbwrv5" data-path="src/components/ai/QuizGenerator.tsx">{question.correct_answer}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
              )}
              </div>
            </div>
          }
        </DialogContent>
      </Dialog>

      {/* Generated Questions Preview */}
      {generatedQuestions.length > 0 &&
      <Card data-id="bumj8jm4w" data-path="src/components/ai/QuizGenerator.tsx">
          <CardHeader data-id="hxv72pwk7" data-path="src/components/ai/QuizGenerator.tsx">
            <CardTitle className="flex items-center justify-between" data-id="c6r4fpj98" data-path="src/components/ai/QuizGenerator.tsx">
              <span data-id="26p1v1gyh" data-path="src/components/ai/QuizGenerator.tsx">Generated Quiz Questions ({generatedQuestions.length})</span>
              <div className="flex gap-2" data-id="mnan4ui7y" data-path="src/components/ai/QuizGenerator.tsx">
                <Button onClick={handleDownloadQuestions} variant="outline" data-id="ylnao0d0g" data-path="src/components/ai/QuizGenerator.tsx">
                  <Download className="h-4 w-4 mr-2" data-id="4j6qmmkol" data-path="src/components/ai/QuizGenerator.tsx" />
                  Download Questions
                </Button>
                <Button onClick={handleDownloadAnswerKey} variant="outline" data-id="7arjo5grm" data-path="src/components/ai/QuizGenerator.tsx">
                  <FileDown className="h-4 w-4 mr-2" data-id="2v4ijp7s2" data-path="src/components/ai/QuizGenerator.tsx" />
                  Download Answer Key
                </Button>
                <Button onClick={handleSaveQuiz} data-id="rjywyvolv" data-path="src/components/ai/QuizGenerator.tsx">
                  <Save className="h-4 w-4 mr-2" data-id="1z07k7fqo" data-path="src/components/ai/QuizGenerator.tsx" />
                  Save Quiz
                </Button>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent data-id="22ahqvkaw" data-path="src/components/ai/QuizGenerator.tsx">
            <div className="space-y-4" data-id="6gzses5va" data-path="src/components/ai/QuizGenerator.tsx">
              {generatedQuestions.map((question, index) =>
            <Card key={index} data-id="q9u52sx3y" data-path="src/components/ai/QuizGenerator.tsx">
                  <CardContent className="pt-4" data-id="i8tzj0h2g" data-path="src/components/ai/QuizGenerator.tsx">
                    <div className="space-y-3" data-id="gfgs0wgm6" data-path="src/components/ai/QuizGenerator.tsx">
                      <div className="flex items-start justify-between" data-id="ymknr6jtf" data-path="src/components/ai/QuizGenerator.tsx">
                        <h4 className="font-medium" data-id="hf4whwjfm" data-path="src/components/ai/QuizGenerator.tsx">Question {index + 1}:</h4>
                        <Badge className={getQuestionTypeColor(question.type)} data-id="rt4l168wp" data-path="src/components/ai/QuizGenerator.tsx">
                          {question.type.replace('_', ' ')}
                        </Badge>
                      </div>
                      <p data-id="mvcrqradh" data-path="src/components/ai/QuizGenerator.tsx">{question.question}</p>

                      {question.options &&
                  <div data-id="4qhlnwc57" data-path="src/components/ai/QuizGenerator.tsx">
                          <p className="font-medium" data-id="t1ocyer7f" data-path="src/components/ai/QuizGenerator.tsx">Options:</p>
                          <ul className="list-disc list-inside" data-id="d05gg7y7y" data-path="src/components/ai/QuizGenerator.tsx">
                            {question.options.map((option, optIndex) =>
                      <li key={optIndex} data-id="etjtwi8mp" data-path="src/components/ai/QuizGenerator.tsx">{option}</li>
                      )}
                          </ul>
                        </div>
                  }

                      <div data-id="yjn17plr6" data-path="src/components/ai/QuizGenerator.tsx">
                        <p className="font-medium" data-id="b7z52gfcu" data-path="src/components/ai/QuizGenerator.tsx">Correct Answer:</p>
                        <p className="text-green-700" data-id="gjrnm3b00" data-path="src/components/ai/QuizGenerator.tsx">{question.correctAnswer}</p>
                      </div>

                      {question.explanation && question.explanation !== question.correctAnswer &&
                  <div data-id="gmd1pb23i" data-path="src/components/ai/QuizGenerator.tsx">
                          <p className="font-medium" data-id="nofx68rhm" data-path="src/components/ai/QuizGenerator.tsx">Explanation:</p>
                          <p className="text-gray-600" data-id="cklg2snzw" data-path="src/components/ai/QuizGenerator.tsx">{question.explanation}</p>
                        </div>
                  }
                    </div>
                  </CardContent>
                </Card>
            )}
            </div>
          </CardContent>
        </Card>
      }
    </div>);

};

export default QuizGenerator;